import { newBinding, matches } from '@boba-cli/key';
import { KeyMsg } from '@boba-cli/tea';

// src/model.ts
var PaginatorType = /* @__PURE__ */ ((PaginatorType2) => {
  PaginatorType2["Arabic"] = "arabic";
  PaginatorType2["Dots"] = "dots";
  return PaginatorType2;
})(PaginatorType || {});
var defaultKeyMap = {
  prevPage: newBinding({ keys: ["pgup", "left", "h"] }),
  nextPage: newBinding({ keys: ["pgdown", "right", "l"] })
};

// src/model.ts
var PaginatorModel = class _PaginatorModel {
  type;
  page;
  perPage;
  totalPages;
  activeDot;
  inactiveDot;
  arabicFormat;
  keyMap;
  constructor(options = {}) {
    this.type = options.type ?? "arabic" /* Arabic */;
    this.page = Math.max(0, options.page ?? 0);
    this.perPage = Math.max(1, options.perPage ?? 1);
    this.totalPages = Math.max(1, options.totalPages ?? 1);
    this.activeDot = options.activeDot ?? "\u2022";
    this.inactiveDot = options.inactiveDot ?? "\u25CB";
    this.arabicFormat = options.arabicFormat ?? "%d/%d";
    this.keyMap = options.keyMap ?? defaultKeyMap;
  }
  /** Create a new paginator with defaults. */
  static new(options = {}) {
    return new _PaginatorModel(options);
  }
  /** Set total pages based on item count (rounded up). */
  setTotalPages(items) {
    if (items < 1) {
      return this;
    }
    const totalPages = Math.max(1, Math.ceil(items / this.perPage));
    return this.with({ totalPages });
  }
  /** Number of items on the current page for a given total. */
  itemsOnPage(totalItems) {
    if (totalItems < 1) {
      return 0;
    }
    const [start, end] = this.getSliceBounds(totalItems);
    return end - start;
  }
  /** Slice bounds for the current page, clamped to length. */
  getSliceBounds(length) {
    const start = this.page * this.perPage;
    const end = Math.min(start + this.perPage, length);
    return [start, end];
  }
  /** Move to previous page (no-op on first page). */
  prevPage() {
    if (this.page <= 0) {
      return this;
    }
    return this.with({ page: this.page - 1 });
  }
  /** Move to next page (no-op on last page). */
  nextPage() {
    if (this.onLastPage()) {
      return this;
    }
    return this.with({ page: this.page + 1 });
  }
  /** Whether the current page is the first page. */
  onFirstPage() {
    return this.page === 0;
  }
  /** Whether the current page is the last page. */
  onLastPage() {
    return this.page >= this.totalPages - 1;
  }
  /** Handle Tea messages (responds to KeyMsg). */
  update(msg) {
    if (!(msg instanceof KeyMsg)) {
      return [this, null];
    }
    if (matches(msg, this.keyMap.nextPage)) {
      return [this.nextPage(), null];
    }
    if (matches(msg, this.keyMap.prevPage)) {
      return [this.prevPage(), null];
    }
    return [this, null];
  }
  /** Render pagination. */
  view() {
    switch (this.type) {
      case "dots" /* Dots */:
        return this.dotsView();
      case "arabic" /* Arabic */:
      default:
        return this.arabicView();
    }
  }
  dotsView() {
    let s = "";
    for (let i = 0; i < this.totalPages; i++) {
      s += i === this.page ? this.activeDot : this.inactiveDot;
    }
    return s;
  }
  arabicView() {
    const current = this.page + 1;
    return this.arabicFormat.replace("%d", String(current)).replace("%d", String(this.totalPages));
  }
  with(patch) {
    return new _PaginatorModel({
      type: this.type,
      page: this.page,
      perPage: this.perPage,
      totalPages: this.totalPages,
      activeDot: this.activeDot,
      inactiveDot: this.inactiveDot,
      arabicFormat: this.arabicFormat,
      keyMap: this.keyMap,
      ...patch
    });
  }
};

export { PaginatorModel, PaginatorType, defaultKeyMap };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map